import { type BodyType } from '@/services/vehicles/types';

/**
 * What the body-type filter offers, and which stock each choice matches.
 *
 * A choice is not always one body type, which is the whole reason this table
 * exists. "Trucks & commercials" is one thing to a buyer and two values in the
 * database (`truck`, `bus`), and the alternative — offering those separately —
 * puts a filter on the page for a category Kabex may hold two units of.
 *
 * Every `BodyType` appears exactly once across the lists, which is what
 * `BODY_CHOICES_COVER_EVERY_TYPE` below makes the compiler check. Exactly once
 * matters in both directions: a type in no choice is unreachable from the
 * filter, and a type in two choices is a car that appears under two labels.
 *
 * Added with the Chinese market (2026-10-18): Sinotruk is the first make on
 * this site whose stock is not a passenger car, and a catalogue that cannot be
 * filtered down to trucks makes the one tile in the menu that promises them
 * land on a page of saloons.
 */
export const bodyChoices = {
  suv: ['suv'],
  sedan: ['sedan'],
  hatchback: ['hatchback'],
  wagon: ['wagon'],
  minivan: ['minivan'],
  van: ['van'],
  pickup: ['pickup'],
  coupe: ['coupe'],
  convertible: ['convertible'],
  commercial: ['truck', 'bus'],
  other: ['other'],
} as const satisfies Record<string, readonly BodyType[]>;

export type BodyChoice = keyof typeof bodyChoices;

/** Every body type any choice matches. */
type CoveredBodyType = (typeof bodyChoices)[BodyChoice][number];

/**
 * Fails to compile when a `BodyType` is missing from the table above — the
 * annotation becomes `false` and the assignment of `true` is an error. Which
 * is the point: a body type added to the catalogue's enum and forgotten here
 * would otherwise be stock no filter can reach, and nothing would say so.
 */
export const BODY_CHOICES_COVER_EVERY_TYPE: Exclude<BodyType, CoveredBodyType> extends never
  ? true
  : false = true;

export const BODY_CHOICE_KEYS = Object.keys(bodyChoices) as [BodyChoice, ...BodyChoice[]];

/**
 * How each choice is named in the filter.
 *
 * Plural, unlike `bodyTypeLabels` in `utils/vehicleFormat.ts`, which names one
 * car ("SUV", "Truck"): these label a set the visitor is asking to see, and
 * "Trucks & commercials" is the one that would read as a single vehicle if it
 * followed the other list's convention.
 */
export const bodyChoiceLabels: Record<BodyChoice, string> = {
  suv: 'SUVs',
  sedan: 'Saloons',
  hatchback: 'Hatchbacks',
  wagon: 'Wagons',
  minivan: 'Minivans',
  van: 'Vans',
  pickup: 'Pickups',
  coupe: 'Coupes',
  convertible: 'Convertibles',
  commercial: 'Trucks & commercials',
  other: 'Other',
};

/**
 * The body types a choice matches. An unknown key matches nothing, so a
 * hand-edited `?body=lorry` shows every car rather than no cars — the same
 * rule the rest of the URL parsing follows.
 */
export function bodyTypesFor(choice: string): readonly BodyType[] | undefined {
  return choice in bodyChoices ? bodyChoices[choice as BodyChoice] : undefined;
}

/**
 * The reverse: which choice a set of body types came from, for filling the
 * form in from the URL.
 *
 * Matched as a set rather than by its first element, so the round trip is
 * lossless in both directions and the form cannot quietly narrow a filter it
 * did not understand — submitting a form prefilled from `?body=commercial`
 * returns `?body=commercial`, not `?body=truck`.
 */
export function bodyChoiceFor(types: readonly BodyType[] | undefined): BodyChoice | '' {
  if (!types?.length) return '';
  const found = BODY_CHOICE_KEYS.find(
    (key) =>
      bodyChoices[key].length === types.length &&
      bodyChoices[key].every((type) => types.includes(type)),
  );
  return found ?? '';
}
